import React, { useEffect, useState, useRef } from "react";
import { Modal } from "react-bootstrap";
import { Formik, Field, setIn } from "formik";
import * as Yup from "yup";
import ReactWebcam from "react-webcam";
import Loading from "../../components/loading";
import { BotonPrimaryText, BotonDangerText, BotonInfoText } from "../../components/BotonesCustom";
import { FieldBasic, SelectVivienda } from "../../components/FieldBasic";
import CustomSelect from "../../components/Select";
import axiosInstance from "../../../../../services/AxiosInstance";
import { toast } from "react-toastify";

import { useVigilanteReloadContext } from "../../../../../context/VisitantesVigilantesContext";

import imagen200300 from '../../../../../images/200x300.png';

const formSchema = Yup.object().shape({
  asunto: Yup.string()
    .required("Por favor, ingresa el asunto")
    .nullable(true),
  nota: Yup.string()
    .required("Por favor, ingresa una nota")
    .nullable(true),
});

const MinutaForm = ({ showModal, onClose }) => {
  const initialValuesEmpty = {
    asunto: "",
    prioridad: "white",
    nota: "",
    foto: null
  };

  const URL = "/api/v3/vigilancia/minuta/";
  const URL_PRIORIDADES = "/api/v3/vigilancia/minuta/dropdown_prioridad/";

  const { setReloadData } = useVigilanteReloadContext();

  const [ initialValues, setInitialValues ] = useState(initialValuesEmpty);
  const [ loadingForm, setLoadingForm ] = useState(false);
  const [ loadingSubmit, setLoadingSubmit ] = useState(false);
  const [ capturedPhoto, setCapturedPhoto ] = useState(null);
  const [ cameraOpen, setCameraOpen ] = useState(false);
  const webcamRef = useRef(null);
  const fileInputRef = useRef(null);

  let submitAction = null;

  useEffect(() => {
    if (!showModal) {
      setCapturedPhoto(null);
      setCameraOpen(false);
    } else {
      fetchPrioridades();
    }
  }, [showModal]);

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    const postData = {
      ...values,
      prioridad: typeof values.prioridad === 'object' ? values.prioridad.value : values.prioridad
    }

    try {
      setLoadingSubmit(false);
      const response = await axiosInstance.post(URL, postData);
      setInitialValues(initialValuesEmpty);
      onClose();
      toast.success("✔️ El ingreso fue exitoso !", {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      onClose();
      resetForm();
      setReloadData(prev => ({ ...prev, minuta: true }));
    } catch (error) {
      handleErrorResponse(error);
    } finally {
      setSubmitting(false);
      setLoadingSubmit(false);
    }
  };

  const handleSaveAndAddAnother = async (values, { setFieldValue, setSubmitting, resetForm }) => {
    const postData = {
      ...values,
      prioridad: typeof values.prioridad === 'object' ? values.prioridad.value : values.prioridad
    }

    try {
      setLoadingSubmit(false);
      const response = await axiosInstance.post(URL, postData);
      setInitialValues(initialValuesEmpty);
      toast.success("✔️ El ingreso fue exitoso !", {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      setCapturedPhoto(null);
      setCameraOpen(false);
      resetForm();
      setReloadData(prev => ({ ...prev, correspondencia: true }));
    } catch (error) {
      handleErrorResponse(error);
    } finally {
      setSubmitting(false);
      setLoadingSubmit(false);
    }
  };

  const handleClearForm = async (values, { setFieldValue, resetForm }) => {
    resetForm();
    setInitialValues(initialValues);
    setCapturedPhoto(null);
    setCameraOpen(false);
  };

  const capturePhoto = (setFieldValue) => {
    if (webcamRef.current) {
      const imageSrc = webcamRef.current.getScreenshot();
      setCapturedPhoto(imageSrc);
      setFieldValue('foto', imageSrc);  // Actualizar el campo foto en Formik
      setCameraOpen(false);
    }
  };

  const handleFileUpload = (event, setFieldValue) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setCapturedPhoto(reader.result);
        setFieldValue('foto', reader.result);  // Actualizar el campo foto en Formik
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current.click();
  };

  function handleErrorResponse(error) {
    let errorMessages = [];

    // Verificar si el error tiene una respuesta y datos
    if (error.response && error.response.data) {
      const errorData = error.response.data;

      if (errorData.type === "validation_error" && Array.isArray(errorData.errors)) {
        errorData.errors.forEach(err => {
          // Combina el atributo y el mensaje de detalle
          const fieldMessage = `${err.attr}: ${err.detail}`;
          errorMessages.push(fieldMessage);
        });
      } else if (errorData.message) {
        errorMessages.push(errorData.message);
      } else {
        errorMessages.push("An unexpected error occurred.");
      }
    } else {
      errorMessages.push(error.message || "An unexpected error occurred.");
    }

    const errorMessage = errorMessages.join("\n");

    // Mostrar el mensaje de error con react-toastify
    toast.error(errorMessage, {
      position: "top-center",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
  };

  // DROPDOWN PRIORIDADES
  const [ prioridadesOptions, setPrioridadesOptions ] = useState([]);
  const fetchPrioridades = async () => {
    try {
      const response = await axiosInstance.get(URL_PRIORIDADES);
      setPrioridadesOptions(response.data);
    } catch (error) {
      console.error('Error en fetchPrioridades:', error);
      handleErrorResponse(error);
    }
  };

  // FIN DROPDOWN PRIORIDADES

  return (
    <Modal show={showModal} size="lg">
      {loadingForm ? (
        <Loading />
      ) : (
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={formSchema}
          onSubmit={(values, { setFieldValue, setSubmitting, isSubmitting, resetForm }) => {
            if (submitAction === "handleSubmit") {
              handleSubmit(values, { setFieldValue, setSubmitting, resetForm });
            } else if (submitAction === "handleSaveAndAddAnother") {
              handleSaveAndAddAnother(values, { setFieldValue, setSubmitting, resetForm });
            }
          }}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            isSubmitting,
            setFieldValue,
            resetForm,
            setSubmitting
          }) => (
            <div role="document">
              <form onSubmit={handleSubmit}>
                <div className="modal-header">
                  <h5 className="modal-title" id="exampleModalLongTitle">
                    Ingreso Minuta
                  </h5>
                </div>

                <div className="modal-body">
                  <div className="row justify-content-center">
                    <div className="container col">
                      <div className="row">
                        <div className="col">
                          <div className="row">
                            <BotonPrimaryText
                              type="button"
                              onClick={() => setCameraOpen(!cameraOpen)}
                            >
                              {cameraOpen ? "Cerrar Cámara" : "Tomar Foto"}
                            </BotonPrimaryText>
                          </div>
                        </div>
                        <div className="col">
                          <div className="row">
                            {cameraOpen ? (
                              <BotonInfoText
                                type="button"
                                onClick={() => capturePhoto(setFieldValue)}
                              >
                                Capturar Foto
                              </BotonInfoText>
                            ) : (
                              <>
                                <input
                                  type="file"
                                  className="form-file-input form-control"
                                  accept="image/*"
                                  ref={fileInputRef}
                                  onChange={(event) => handleFileUpload(event, setFieldValue)}
                                  style={{ display: 'none' }}
                                />
                                <BotonPrimaryText
                                  type="button"
                                  onClick={handleUploadClick}
                                >
                                  Subir Foto
                                </BotonPrimaryText>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="col">
                        <div className="p-2">
                          <div style={{ height: 300, textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {cameraOpen ? (
                              <ReactWebcam
                                audio={false}
                                ref={webcamRef}
                                screenshotFormat="image/jpeg"
                                width={200}
                                height={300}
                                minScreenshotHeight={1024}
                                minScreenshotWidth={1536}
                                style={{
                                  objectFit: 'cover',
                                }}
                                videoConstraints={{
                                  width: 1024,
                                  height: 1536,
                                  facingMode: "user"
                                }}
                              />
                            ) : (
                              <div style={{ height: '100%' }}>
                                {capturedPhoto ? (
                                  <img
                                    src={capturedPhoto}
                                    className="img-fluid"
                                    alt="Captured"
                                    style={{
                                      width: '200px',
                                      height: '300px',
                                      objectFit: 'cover',
                                    }}
                                  />
                                ) : (
                                  <img
                                    src={imagen200300}
                                    className="img-fluid"
                                    alt="Placeholder"
                                    style={{
                                      objectFit: 'cover',
                                    }}
                                  />
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-md">
                      <div className="p-2">
                          <div className="form-group pb-2">
                          <FieldBasic
                            name="Asunto"
                            required
                            errors={errors.asunto}
                          >
                            <Field
                              type="text"
                              name="asunto"
                              id="asunto"
                              autoComplete="off"
                              className="form-control"
                              placeholder="Asunto"
                              onChange={handleChange}
                              onBlur={handleBlur}
                              value={values.asunto}
                            />
                          </FieldBasic>
                        </div>
                        <div className="form-group pb-2">
                          <FieldBasic
                            name="nota"
                            required
                            errors={errors.nota}
                          >
                            <Field
                              type="text"
                              name="nota"
                              id="nota"
                              autoComplete="off"
                              className="form-control"
                              placeholder="Nota"
                              onChange={handleChange}
                              onBlur={handleBlur}
                              value={values.nota}
                            />
                          </FieldBasic>
                        </div>
                        <div className="form-group pb-2">
                          <CustomSelect
                            name="prioridad"
                            id="prioridad"
                            defaultsValues={values.prioridad}
                            options={prioridadesOptions}
                            value={prioridadesOptions.find(opt => opt.value === values.prioridad)}
                            onChange={(selectedOption) => {
                              setFieldValue("prioridad", selectedOption ? selectedOption.value : "white");
                            }}
                            placeholder="Seleccione prioridad"
                            isSearchable={false}
                            styles={{
                              option: (provided, state) => ({
                                ...provided,
                                color: 'black',
                                backgroundColor: state.isSelected ? '#007bff' : 'white'
                              }),
                              singleValue: (provided) => ({
                                ...provided,
                                color: 'black'
                              })
                            }}
                            classNamePrefix="select"
                            isClearable={false}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  {loadingSubmit ? (
                    <div className="text-center">
                      <Loading />
                    </div>
                  ) : (
                    <>
                      <BotonDangerText
                        type="button"
                        onClick={onClose}
                      >
                        Cancelar
                      </BotonDangerText>
                      <BotonDangerText
                        type="button"
                        onClick={() => handleClearForm(values, { setFieldValue, setSubmitting, resetForm })}
                        disabled={isSubmitting}
                      >
                        Limpiar
                      </BotonDangerText>
                      <BotonPrimaryText
                        type="submit"
                        disabled={isSubmitting}
                        onClick={() => {
                          submitAction = "handleSubmit";
                        }}
                      >
                        Guardar
                      </BotonPrimaryText>
                      <BotonPrimaryText
                        type="submit"
                        disabled={isSubmitting}
                        onClick={() => {
                          submitAction = "handleSaveAndAddAnother";
                          // return handleSaveAndAddAnother(values, { setFieldValue, setSubmitting, isSubmitting, resetForm })
                        }}
                      >
                        Guardar y añadir otro
                      </BotonPrimaryText>
                    </>
                  )}
                </div>
              </form>
            </div>
          )}
        </Formik>
      )}
    </Modal>
  );
};

export default MinutaForm;
